import { ATTENTION_STATUS_BITS } from 'partner360/entities/attentionStatuses'

import type { IAttentionStatusesItem } from '../../types'

import { AcknowledgmentNextSteps } from './AcknowledgmentNextSteps'
import { AttentionNextStepsGroup } from './AttentionNextStepsGroup'
import { hasManagerNextSteps, ManagerNextSteps } from './ManagerNextSteps'
import { PlannedNextSteps } from './PlannedNextSteps'
import { SobNextSteps } from './SobNextSteps'

import styles from './AttentionNextSteps.module.scss'

/** Whose statuses the list holds: one partner's, or those raised across the manager's partners. */
export type TAttentionNextStepsScope = 'partner' | 'manager'

interface IAttentionNextStepsListProps {
  items: IAttentionStatusesItem[]
  onSelectItem: (id: string) => void
  scope?: TAttentionNextStepsScope
  /** Called once a step opened a partner's status in the tile, so a collapsed tile expands. */
  onOpenPartnerDetail?: () => void
}

/**
 * The tile's statuses, each with its top next steps under it, so the reader acts from the tile
 * rather than opening every status first: a partner's own steps on its cockpit, the partners to
 * start with on the manager cockpit. A status with no steps of its own keeps its line and the way
 * into its detail.
 */
export const AttentionNextStepsList = ({
  items,
  onSelectItem,
  scope = 'partner',
  onOpenPartnerDetail,
}: IAttentionNextStepsListProps) => (
  <div className={styles.list}>
    {items.map((item) => {
      const { bitValue, offering } = item

      if (scope === 'manager') {
        return hasManagerNextSteps(bitValue) ? (
          <ManagerNextSteps
            key={item.id}
            item={item}
            onSelect={onSelectItem}
            onOpenPartnerDetail={onOpenPartnerDetail}
          />
        ) : (
          <AttentionNextStepsGroup key={item.id} item={item} onSelect={onSelectItem}>
            {null}
          </AttentionNextStepsGroup>
        )
      }

      if (bitValue === ATTENTION_STATUS_BITS.planned) {
        return <PlannedNextSteps key={item.id} item={item} onSelect={onSelectItem} />
      }

      if (bitValue === ATTENTION_STATUS_BITS.sobActuals && offering) {
        return <SobNextSteps key={item.id} item={{ ...item, offering }} onSelect={onSelectItem} />
      }

      if (bitValue === ATTENTION_STATUS_BITS.acknowledgment) {
        return <AcknowledgmentNextSteps key={item.id} item={item} onSelect={onSelectItem} />
      }

      return (
        <AttentionNextStepsGroup key={item.id} item={item} onSelect={onSelectItem}>
          {null}
        </AttentionNextStepsGroup>
      )
    })}
  </div>
)

AttentionNextStepsList.displayName = 'AttentionNextStepsList'
